import React, { useCallback, useState, useEffect } from 'react';
import { Button } from '@trello/nachos/button';
import { forTemplate } from '@trello/i18n';
import { useAddBoardStarMutation } from './AddBoardStarMutation.generated';
import { useRemoveBoardStarMutation } from './RemoveBoardStarMutation.generated';
import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import isEqual from 'react-fast-compare';
import styles from './StarredBoardButton.less';
import { StarredIcons } from './StarredIcons';
import { IconColor } from '@trello/nachos/icon';

const format = forTemplate('board_item');

interface StarredBoardButtonProps {
  readonly idBoard: string;
  readonly memberId: string;
  readonly maxBoardStarPosition: number;
  readonly boardStarId?: string;
  readonly onClick?: () => void;
  readonly starColor?: IconColor | undefined;
}

const sendAnalyticsError = (idBoard: string, error: Error) => {
  Analytics.sendOperationalEvent({
    action: 'errored',
    actionSubject: 'viewsSwitcherCalloutCache',
    source: 'boardsMenuInlineDialog',
    containers: formatContainers({ idBoard }),
    attributes: {
      message: error?.message,
      name: error?.name,
    },
  });
};

export const StarredBoardButton: React.FunctionComponent<StarredBoardButtonProps> = ({
  onClick,
  starColor,
  ...boardFromCache
}) => {
  const [previousBoardFromCache, setPreviousBoardFromCache] = useState(
    boardFromCache,
  );
  const [board, setBoard] = useState(boardFromCache);

  useEffect(() => {
    const cacheUpdate = !isEqual(boardFromCache, previousBoardFromCache);
    if (cacheUpdate) {
      setPreviousBoardFromCache(boardFromCache);
      setBoard(boardFromCache);
    }
  }, [boardFromCache, previousBoardFromCache]);

  const [removeBoardStar] = useRemoveBoardStarMutation();
  const [addBoardStar] = useAddBoardStarMutation();

  const clickHandler = useCallback(async () => {
    const {
      boardStarId,
      memberId,
      idBoard: boardId,
      maxBoardStarPosition,
    } = boardFromCache;
    try {
      if (boardStarId) {
        setBoard({
          ...boardFromCache,
          boardStarId: undefined,
        });
        await removeBoardStar({
          variables: {
            memberId,
            boardStarId,
          },
        });
      } else {
        setBoard({
          ...boardFromCache,
          boardStarId: 'boardStarId',
        });
        await addBoardStar({
          variables: {
            memberId,
            boardId,
            pos: 1 + maxBoardStarPosition,
          },
        });
      }
      if (onClick) {
        onClick();
      }
    } catch (error) {
      sendAnalyticsError(boardId, error);
      setBoard(boardFromCache);
    }
  }, [boardFromCache, removeBoardStar, addBoardStar, onClick, setBoard]);

  const onClickHandler = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      clickHandler();
    },
    [clickHandler],
  );
  const onKeyDownHandler = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        clickHandler();
      }
    },
    [clickHandler],
  );

  return (
    <Button
      appearance="transparent"
      className={styles.button}
      iconBefore={
        <StarredIcons isStarred={!!board?.boardStarId} starColor={starColor} />
      }
      onClick={onClickHandler}
      onKeyDown={onKeyDownHandler}
      title={format(board?.boardStarId ? 'click-to-unstar' : 'click-to-star')}
    />
  );
};
